// =====================================================================================
// INVRT Radar — web console — sites list
//
// The visitor-intelligence entry point. Pixel status is the honest signal the API gives:
// `pending` until a verify probe finds the marker or a real event lands, `verified` only
// once an actual visitor event has arrived — the strongest evidence there is, and one the
// API never lets regress.
// =====================================================================================

import { Link } from "react-router-dom";
import { Globe, Plus } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient, useSites } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { formatDate } from "@/lib/format";
import { trialLabel } from "@/lib/trialLabel";
import type { PixelStatus, Site } from "@/lib/types";

const PIXEL_COPY: Record<PixelStatus, { tone: "neutral" | "info" | "success"; label: string }> = {
  pending: { tone: "neutral", label: "Pixel not detected yet" },
  installed: { tone: "info", label: "Pixel found on the page" },
  verified: { tone: "success", label: "Verified — visitors arriving" },
};

function SiteRow({ site }: { site: Site }) {
  const pixel = PIXEL_COPY[site.pixel_status];

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-5">
        <div className="flex min-w-0 items-start gap-3">
          <Globe className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" aria-hidden="true" />
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{site.domain}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={pixel.tone}>{pixel.label}</Badge>
              {site.trial_state ? <Badge tone="info">{trialLabel(site.trial_state)}</Badge> : null}
              {site.budget_ids_month !== null ? (
                <Badge tone="neutral">
                  {site.budget_ids_month.toLocaleString("en-GB")} identifications a month
                </Badge>
              ) : null}
              <Badge tone="neutral">
                {site.consent_mode === "radar_banner"
                  ? "Consent banner published by Radar"
                  : "Your own consent platform"}
              </Badge>
            </div>
            <p className="mt-1.5 text-xs text-ink-faint">
              Added {formatDate(site.created_at)}
            </p>
          </div>
        </div>
        <Link
          to={`/sites/${site.id}`}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
        >
          Open site
        </Link>
      </CardContent>
    </Card>
  );
}

function ProvisionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const [domain, setDomain] = useState("");
  const [planType, setPlanType] = useState<"b2b" | "b2c">("b2b");
  const [consentMode, setConsentMode] = useState<"radar_banner" | "existing_cmp">("radar_banner");

  const provision = useMutation({
    mutationFn: () =>
      api.post<{ site_id: string; ingest_url: string }>("/sites", {
        domain: domain.trim(),
        plan_type: planType,
        consent_mode: consentMode,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sites", orgId] });
      setDomain("");
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a site"
      description="Radar creates the visitor-resolution account and hands you an install snippet. It never edits your site for you."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={provision.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => provision.mutate()}
            loading={provision.isPending}
            disabled={domain.trim() === ""}
          >
            Add site
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Domain"
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
          placeholder="example.com"
          hint="Just the hostname — no https://, no path."
        />
        <Select
          label="What are you identifying?"
          value={planType}
          onChange={(event) => setPlanType(event.target.value as "b2b" | "b2c")}
          hint="Person-level identification is available in the United States only. Everywhere else, Radar names the company."
        >
          <option value="b2b">Businesses visiting the site</option>
          <option value="b2c">Consumers visiting the site</option>
        </Select>
        <Select
          label="How is consent handled?"
          value={consentMode}
          onChange={(event) =>
            setConsentMode(event.target.value as "radar_banner" | "existing_cmp")
          }
          hint="Required — a site cannot start collecting until this is answered. Confirm your own obligations with whoever signs off compliance."
        >
          <option value="radar_banner">Radar publishes a cookie banner for me</option>
          <option value="existing_cmp">I already run a consent platform</option>
        </Select>
        {provision.isError ? <ErrorState error={provision.error} /> : null}
      </div>
    </Dialog>
  );
}

export function SitesPage() {
  const sites = useSites();
  const { activeMembership } = useSession();
  const [adding, setAdding] = useState(false);
  const isAdmin = activeMembership?.is_org_admin === true;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Site visitors</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Name the companies already looking at your site. Radar never bills you for a
            visitor who is already on your known-customer list.
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add a site
          </Button>
        ) : null}
      </div>

      {sites.isLoading ? <LoadingState label="Loading your sites…" /> : null}
      {sites.isError ? <ErrorState error={sites.error} onRetry={() => void sites.refetch()} /> : null}

      {sites.isSuccess ? (
        sites.data.sites.length === 0 ? (
          <EmptyState
            title="No sites yet"
            body={
              isAdmin
                ? "Add your domain and Radar hands back an install snippet plus a guide for whichever platform your site runs on."
                : "An administrator on your team can add a site. Once the pixel is live, identified visitors appear here."
            }
            {...(isAdmin
              ? { action: <Button onClick={() => setAdding(true)}>Add a site</Button> }
              : {})}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {sites.data.sites.map((site) => (
              <SiteRow key={site.id} site={site} />
            ))}
          </div>
        )
      ) : null}

      <ProvisionDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}
